// /login — credentials form (design/look-and-feel/10-entry-login.html).
// MX Spanish. Copy strings here are the contract asserted by e2e/login.spec.ts
// ("Entrar", "Correo electrónico", "Contraseña", "Iniciar sesión") — keep them.

import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { type FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { postLogin } from '../api/client.js'
import { describeError } from '../api/describe-error.js'
import { contextFromToken, homePathForContext } from '../auth/session.js'
import { useAuthStore } from '../auth/store.js'
import { Alert, Button, FormField, Input, PasswordInput } from '../design-system/index.js'
import { AuthLayout } from './auth/AuthLayout.js'

/** Login errors with the lockout special case: the server sends locked_until,
 *  and "come back in ~N minutos" turns a dead-end into a wait — checkpoint-1
 *  finding: the generic message left no clue the block was temporary. */
function describeLoginError(err: unknown): string {
  if (isAxiosError(err)) {
    const body = err.response?.data as
      | { error?: { code?: string; message?: string; details?: { locked_until?: string } } }
      | undefined
    if (body?.error?.code === 'auth_account_locked' && body.error.details?.locked_until) {
      const msLeft = new Date(body.error.details.locked_until).getTime() - Date.now()
      const minutes = Math.max(1, Math.ceil(msLeft / 60_000))
      return `Cuenta bloqueada por demasiados intentos fallidos. Intenta de nuevo en ${minutes} ${
        minutes === 1 ? 'minuto' : 'minutos'
      }.`
    }
  }
  return describeError(err)
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.1}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-4 text-text-3"
    >
      <rect x="3.5" y="11" width="17" height="10" rx="2.2" />
      <path d="M7.5 11V7.5a4.5 4.5 0 0 1 9 0V11" />
    </svg>
  )
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setSession = useAuthStore((s) => s.setSession)
  const setMfaChallenge = useAuthStore((s) => s.setMfaChallenge)
  const setPasswordChange = useAuthStore((s) => s.setPasswordChange)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const result = await postLogin(email, password)
      if (result.data.step === 'complete') {
        setSession({
          accessToken: result.data.access_token,
          refreshToken: result.data.refresh_token,
          user: result.data.user,
        })
        // Drop any query cache from a previous session so the post-login pages
        // fetch fresh with the new token.
        queryClient.clear()
        // Route by role: operators (no tenant membership) must land on the
        // console overview, not the tenant `/home` (GET /home 404s for them —
        // that was the "panorama won't load" spinner).
        void navigate(homePathForContext(contextFromToken(result.data.access_token)), {
          replace: true,
        })
      } else if (result.data.step === 'password_change') {
        // Direct-created account on its temp password: no session yet — the
        // person creates their own password first.
        setPasswordChange(result.data.password_change_token)
        void navigate('/crear-contrasena', { replace: true })
      } else {
        setMfaChallenge(result.data.mfa_challenge_token)
        void navigate('/mfa', { replace: true })
      }
    } catch (err) {
      setError(describeLoginError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      notice={
        <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-line bg-surface-2 p-3">
          <LockIcon />
          <p className="text-[11.5px] leading-relaxed text-text-3">
            INHERTA es una plataforma privada.{' '}
            <b className="font-semibold text-text-2">Tu acceso lo crea y administra tu proveedor</b>
            ; si tienes dudas, contáctalo directamente.
          </p>
        </div>
      }
    >
      <h1 className="text-[26px] font-bold tracking-[-0.025em] text-text">Iniciar sesión</h1>
      <p className="mt-1.5 text-[13px] text-text-2">Entra a tu espacio de trabajo en INHERTA.</p>

      {error !== null && (
        <Alert tone="sens" className="mt-5">
          {error}
        </Alert>
      )}

      <form onSubmit={(e) => void onSubmit(e)} className="mt-6 flex flex-col gap-4" noValidate>
        <FormField label="Correo electrónico" htmlFor="email">
          <Input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            placeholder="tu@email.com"
          />
        </FormField>

        <div>
          {/* PasswordInput adds the show/hide eye toggle. Its toggle button is
              labelled "Mostrar/Ocultar contraseña", which substring-collides
              with getByLabel('Contraseña') — the E2E locators use { exact: true }
              so they still resolve only the field. */}
          <FormField label="Contraseña" htmlFor="password">
            <PasswordInput
              id="password"
              showToggle
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </FormField>
          <a
            href="/reset-password"
            className="mt-1.5 inline-block text-[11.5px] text-text-3 hover:text-link hover:underline"
          >
            ¿Olvidaste tu contraseña?
          </a>
        </div>

        <Button
          type="submit"
          block
          loading={submitting}
          disabled={email.length === 0 || password.length === 0}
        >
          Entrar
        </Button>
      </form>
    </AuthLayout>
  )
}
